import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@src/app/store";
import { useResetSyncTimestampMutation } from "@src/api/LoginApiSlice";
import { toast } from "top-brains-ui";
import { isElectronApp } from "@src/utils/envUtils";

const isEditableElement = (el: HTMLElement | null): boolean => {
  if (!el) return false;
  const tagName = el.tagName?.toLowerCase();
  if (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    el.isContentEditable ||
    el.getAttribute("contenteditable") === "true" ||
    el.getAttribute("role") === "textbox"
  ) {
    return true;
  }
  if (
    el.closest(
      'input, textarea, select, [contenteditable="true"], [role="textbox"], .monaco-editor, .terminal, .xterm, .cm-editor, .ace_editor, [data-slate-editor="true"], [data-editor="true"]',
    )
  ) {
    return true;
  }
  return false;
};

export const useGlobalElectronSync = () => {
  const [resetSyncTimestamp] = useResetSyncTimestampMutation();
  const userDetails = useSelector(
    (state: RootState) => state.globalSlice.userDetails,
  );
  const isSyncingRef = useRef(false);
  const userDetailsRef = useRef(userDetails);

  useEffect(() => {
    userDetailsRef.current = userDetails;
  }, [userDetails]);

  useEffect(() => {
    if (!isElectronApp()) return;

    const handleKeyDown = async (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const activeEl = document.activeElement as HTMLElement | null;

      if (isEditableElement(target) || isEditableElement(activeEl)) {
        return;
      }

      // Shortcut: Ctrl + Alt + S or Ctrl + Shift + S (or Cmd on macOS)
      // Non-conflicting across Linux and Windows
      const isKeyS = e.code === "KeyS" || e.key === "s" || e.key === "S";
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;
      const isAltOrShift = e.altKey || e.shiftKey;

      if (isCtrlOrCmd && isAltOrShift && isKeyS) {
        e.preventDefault();
        e.stopPropagation();

        console.log(
          "[useGlobalElectronSync] Shortcut triggered! (ctrl/cmd:",
          isCtrlOrCmd,
          "alt/shift:",
          isAltOrShift,
          ")",
        );

        if (isSyncingRef.current) {
          console.warn("[useGlobalElectronSync] Sync already in progress, skipping duplicate.");
          return;
        }

        const user = userDetailsRef.current;
        const userId =
          user?.id ||
          (user as any)?._id ||
          (user as any)?.studentId ||
          (user as any)?.userId;

        if (!userId) {
          console.error("[useGlobalElectronSync] User ID not found in userDetails:", user);
          toast.error("Error", "User ID not found. Please log in again.");
          return;
        }

        console.log("[useGlobalElectronSync] Calling resetSyncTimestamp for userId:", userId);
        isSyncingRef.current = true;
        toast.info(
          "Syncing",
          "Resetting sync timestamp and triggering synchronization...",
        );

        try {
          await resetSyncTimestamp({ userId }).unwrap();
          console.log("[useGlobalElectronSync] resetSyncTimestamp API call succeeded.");

          if (window.electronApp?.syncData) {
            console.log("[useGlobalElectronSync] Invoking electronApp.syncData()...");
            const result = await window.electronApp.syncData();
            if (result && result.success) {
              toast.success("Success", "Data synchronized successfully.");
            } else {
              toast.error("Error", result?.error || "Sync failed.");
            }
          } else {
            toast.success("Success", "Sync timestamp reset successfully.");
          }
        } catch (err: any) {
          console.error("[useGlobalElectronSync] Sync failed with error:", err);
          toast.error(
            "Error",
            err.data?.response || err.message || "Failed to trigger sync.",
          );
        } finally {
          isSyncingRef.current = false;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [resetSyncTimestamp]);
};

export default useGlobalElectronSync;
